import { useState, useEffect, useCallback, useRef } from 'react';
import { IPlace, IPlacesResponse } from '@/interfaces/property';
import { getCurrentLanguage } from '@/lib/i18n';
import { API_BASE } from '@/lib/api';
import { traceApiError, traceApiResponse } from '@/lib/api-tracing';

interface UsePlacesOptions {
  propertyId: string;
  placeType?: string;
  limit?: number;
  propertyLat?: number;
  propertyLon?: number;
}

interface UsePlacesReturn {
  places: IPlace[];
  loading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  refetch: () => void;
  reset: () => void;
}

export const usePlaces = ({ propertyId, placeType, limit = 10, propertyLat, propertyLon }: UsePlacesOptions): UsePlacesReturn => {
  const [places, setPlaces] = useState<IPlace[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const loadingRef = useRef(false);
  const lastLoadTimeRef = useRef(0);
  const reachedEndRef = useRef(false);

  const fetchPlaces = useCallback(async (cursor?: string, append = false) => {
    if (loadingRef.current) return;
    
    // Don't fetch if we've already reached the end
    if (reachedEndRef.current && !cursor) {
      return;
    }

    setLoading(true);
    loadingRef.current = true;
    setError(null);
    const startTime = performance.now();

    const params = new URLSearchParams();
    if (cursor) params.append('cursor', cursor);
    if (placeType && placeType !== 'all') params.append('place_type', placeType);
    params.append('limit', limit.toString());
    params.append('max_distance', '500');
    
    // Add property coordinates for distance calculation
    if (propertyLat !== undefined && propertyLon !== undefined) {
      params.append('lat', propertyLat.toString());
      params.append('lon', propertyLon.toString());
    }

    const url = `${API_BASE}/property/${propertyId}/places?${params.toString()}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': getCurrentLanguage(),
        },
      });

      if (!response.ok) {
        const duration = performance.now() - startTime;
        traceApiError(
          url,
          'GET',
          new Error(`HTTP ${response.status}: ${response.statusText}`),
          { propertyId, cursor, placeType, propertyLat, propertyLon, operation: 'fetch_places', duration }
        );
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: IPlacesResponse = await response.json();
      const duration = performance.now() - startTime;
      traceApiResponse(
        url,
        'GET',
        response,
        data,
        duration,
        { propertyId, cursor, placeType, propertyLat, propertyLon, operation: 'fetch_places' }
      );

      if (append) {
        setPlaces(prev => {
          // Create a Set of existing place IDs to avoid duplicates
          const existingIds = new Set(prev.map(place => place.id));
          // Filter out duplicates from new places
          const newPlaces = data.places.filter(place => !existingIds.has(place.id));
          // Adding new places
          return [...prev, ...newPlaces];
        });
      } else {
        setPlaces(data.places);
      }
      
      // Update pagination state
      if (!data.pagination.has_next) {
        reachedEndRef.current = true;
        setHasMore(false);
        setNextCursor(undefined);
      } else {
        setHasMore(true);
        setNextCursor(data.pagination.next_cursor || undefined);
      }
    } catch (err) {
      const duration = performance.now() - startTime;
      traceApiError(
        url,
        'GET',
        err as Error,
        { propertyId, cursor, placeType, propertyLat, propertyLon, operation: 'fetch_places', duration }
      );
      
      setError(err instanceof Error ? err.message : 'Failed to fetch places');
      console.error('Error fetching places:', err);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [propertyId, placeType, limit, propertyLat, propertyLon]);

  const loadMore = useCallback(() => {
    const now = Date.now();
    const timeSinceLastLoad = now - lastLoadTimeRef.current;
    
    // Debounce: prevent loading more than once every 300ms
    if (timeSinceLastLoad < 300) {
      return;
    }
    
    // Check if we've already reached the end
    if (reachedEndRef.current) {
      return;
    }
    
    // Check if we're currently loading
    if (loadingRef.current) {
      return;
    }
    
    // Check if we have a cursor to load more
    if (!nextCursor) {
      return;
    }
    
    // Loading more places
    lastLoadTimeRef.current = now;
    fetchPlaces(nextCursor, true);
  }, [nextCursor, fetchPlaces]);

  const refetch = useCallback(() => {
    setPlaces([]);
    setNextCursor(undefined);
    setHasMore(true);
    reachedEndRef.current = false;
    fetchPlaces();
  }, [fetchPlaces]);

  const reset = useCallback(() => {
    setPlaces([]);
    setNextCursor(undefined);
    setHasMore(true);
    reachedEndRef.current = false;
    loadingRef.current = false;
    lastLoadTimeRef.current = 0;
  }, []);

  // Initial fetch
  useEffect(() => {
    setPlaces([]);
    setNextCursor(undefined);
    setHasMore(true);
    reachedEndRef.current = false;
    fetchPlaces();
  }, [propertyId, placeType, fetchPlaces]);

  return {
    places,
    loading,
    hasMore,
    loadMore,
    refetch,
    reset,
  };
}; 